/**
 * Date formatting shared by the payment and activity screens. These were
 * previously re-declared inside PaymentModal, ActivityModal and ActivityTable.
 */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ordinal = (n: number) =>
  n +
  ["th", "st", "nd", "rd"][
    n % 10 > 3 || Math.floor((n % 100) / 10) === 1 ? 0 : n % 10
  ];

/** "12th July, 2026" */
export const formatLongDate = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${ordinal(date.getDate())} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
};

/** "Today • 4:05 PM" / "Yesterday • 4:05 PM" / "Jul 12 • 4:05 PM" */
export const formatRelativeDateTime = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (sameDay(date, now)) return `Today • ${time}`;
  if (sameDay(date, yesterday)) return `Yesterday • ${time}`;

  const monthDay = date.toLocaleString("default", {
    month: "short",
    day: "numeric",
  });
  return `${monthDay} • ${time}`;
};

/** "Jul 12th, 2026, 4:05 PM" — the shape used in CSV exports. */
export const formatExportDate = (value?: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.toLocaleString("default", { month: "short" })} ${ordinal(
    date.getDate()
  )}, ${date.getFullYear()}, ${date.toLocaleString("default", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  })}`;
};

/** Shortens a hash/address for table cells: "0x1234…cdef" */
export const truncateMiddle = (value?: string, lead = 6, tail = 4): string => {
  if (!value) return "—";
  if (value.length <= lead + tail + 1) return value;
  return `${value.slice(0, lead)}…${value.slice(-tail)}`;
};
